import type { FC, ReactNode } from 'react'
import { Autocomplete, TextField, Typography, Box } from '@mui/material'
import { ReferralNavigationServiceCategoriesListResponse } from '@fireflyhealth/api'
import { useNavigationServiceCategories } from '~/entities/steerage'

type NavigationServiceCategory = ReferralNavigationServiceCategoriesListResponse[number]

interface NavigationServiceCategoryAutocompleteProps {
  onChange: (value: NavigationServiceCategory | null) => void
  selectedCategory: NavigationServiceCategory | null
  disabled?: boolean
  textFieldVariant?: 'outlined' | 'standard'
  label?: string
  required?: boolean
  error?: boolean
  helperText?: ReactNode
  onBlur?: () => void
}

const NavigationServiceCategoryAutocomplete: FC<
  NavigationServiceCategoryAutocompleteProps
> = props => {
  const { data: categories = [] } = useNavigationServiceCategories()

  return (
    <Autocomplete
      disabled={props?.disabled}
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      multiple={false}
      size="small"
      options={categories}
      getOptionLabel={option => option?.name ?? ''}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderInput={params => (
        <TextField
          {...params}
          variant={props?.textFieldVariant ?? 'standard'}
          fullWidth
          label={props?.label ?? 'Service Category'}
          required={props?.required}
          error={props?.error}
          helperText={props?.helperText}
          onBlur={props?.onBlur}
        />
      )}
      renderOption={({ key, ...liProps }, option) => (
        <li key={key} {...liProps}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography component="span" sx={{ fontSize: '1.4rem', alignSelf: 'flex-start' }}>
              {option.name}
            </Typography>
            {option.examples ? (
              <Typography sx={{ fontSize: '1.2rem', color: 'grey.500', alignSelf: 'flex-start' }}>
                {option.examples}
              </Typography>
            ) : null}
          </Box>
        </li>
      )}
      value={props.selectedCategory ?? null}
      onChange={(_event, value) => {
        if (value == null) {
          props?.onChange(null)
        } else if (typeof value.id === 'number') {
          props?.onChange(value)
        }
      }}
    />
  )
}

export default NavigationServiceCategoryAutocomplete
